/**
 * Drop-in sortable + filterable table headers.
 *
 *  Modern usage (sort + per-column text filter):
 *      const ctrl = useTableControls(items);
 *      <SortableTh ctrl={ctrl} field="name">Name</SortableTh>
 *      {ctrl.rows.map(r => …)}
 *
 *  Legacy sort-only API (still supported — keeps existing callers working):
 *      const [sort, setSort] = useSortState("created_at", "desc");
 *      const sorted = useMemo(() => sortRows(items, sort), [items, sort]);
 *      <SortableTh sort={sort} setSort={setSort} field="name">Name</SortableTh>
 *
 * Behaviour:
 *  - Click header label cycles: asc → desc → unsorted (returns to natural order)
 *  - Filter icon (next to label) opens a popover with a text input that
 *    case-insensitively substring-matches the column's accessor value.
 *  - Active sort column shows a gold ↑ / ↓ icon. Active filter column
 *    paints the filter icon gold.
 *  - Supports nested fields via dot path: field="user.email"
 *  - Robust to undefined / null values (always sorted last, never match a
 *    filter unless the filter is empty).
 *  - Numbers compared numerically; strings case-insensitive; ISO datetimes
 *    sortable as strings.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Filter, X } from "lucide-react";

/* ------------------------------------------------------------------ helpers */

function getNested(obj, path) {
    if (!path || obj == null) return undefined;
    return String(path).split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
}

function isNumericString(v) {
    if (typeof v === "number") return true;
    const s = String(v).trim();
    return s !== "" && /^-?\d+(\.\d+)?$/.test(s);
}

/* ------------------------------------------------------ legacy sort API */

export function useSortState(defaultField = null, defaultDir = "asc") {
    return useState({ field: defaultField, dir: defaultDir });
}

export function sortRows(rows, sort) {
    if (!sort?.field || !Array.isArray(rows)) return rows;
    const sign = sort.dir === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => {
        const av = getNested(a, sort.field);
        const bv = getNested(b, sort.field);
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * sign;
        if (typeof av === "boolean" && typeof bv === "boolean") return (Number(av) - Number(bv)) * sign;
        const sa = String(av).toLowerCase();
        const sb = String(bv).toLowerCase();
        if (sa < sb) return -1 * sign;
        if (sa > sb) return 1 * sign;
        return 0;
    });
}

export function useSortedRows(rows, defaultField = null, defaultDir = "asc") {
    const [sort, setSort] = useSortState(defaultField, defaultDir);
    const sorted = useMemo(() => sortRows(rows, sort), [rows, sort]);
    return { sort, setSort, sorted };
}

/* ----------------------------------------------- combined sort + filter API */

/**
 * useTableControls(rows, options?)
 *   - rows: raw array
 *   - options.accessors: { [key]: (row) => sortableValue } overrides
 *   - options.initialSort: { key, dir }
 *   - options.storageKey: when provided, sort state + per-column filters are
 *     persisted to localStorage under this key and restored on next mount,
 *     so the user's working view survives page reloads / tab switches.
 *
 *  Returns { rows (view), sort, setSort, sortKey, sortDir, toggleSort,
 *           filters, setFilter, clearFilters, hasAnyFilter }.
 *
 *  `sort`/`setSort` shape matches the legacy useSortState contract so the
 *  same <SortableTh> can drive it.
 */
export function useTableControls(rows = [], options = {}) {
    const { accessors = {}, initialSort = null, storageKey = null } = options;

    const loadPersisted = () => {
        if (!storageKey || typeof window === "undefined") return null;
        try {
            const raw = window.localStorage.getItem(`tbl:${storageKey}`);
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    };

    const persisted = storageKey ? loadPersisted() : null;
    const [sort, setSort] = useState(() =>
        persisted?.sort && (persisted.sort.field || persisted.sort.field === null)
            ? persisted.sort
            : { field: initialSort?.key || null, dir: initialSort?.dir || "asc" }
    );
    const [filters, setFilters] = useState(() => persisted?.filters || {});

    useEffect(() => {
        if (!storageKey || typeof window === "undefined") return;
        try {
            window.localStorage.setItem(`tbl:${storageKey}`, JSON.stringify({ sort, filters }));
        } catch { /* quota / disabled localStorage — ignore */ }
    }, [storageKey, sort, filters]);

    const toggleSort = (key) => {
        if (sort.field !== key) { setSort({ field: key, dir: "asc" }); return; }
        if (sort.dir === "asc") { setSort({ field: key, dir: "desc" }); return; }
        setSort({ field: null, dir: "asc" });
    };
    const setFilter = (key, val) => setFilters((prev) => ({ ...prev, [key]: val }));
    const clearFilters = () => setFilters({});

    const resolve = (row, key) => (accessors[key] ? accessors[key](row) : getNested(row, key));

    const view = useMemo(() => {
        let r = Array.isArray(rows) ? rows : [];
        const fEntries = Object.entries(filters).filter(([, v]) => v != null && String(v).trim() !== "");
        if (fEntries.length) {
            r = r.filter((row) =>
                fEntries.every(([k, q]) => {
                    const v = resolve(row, k);
                    if (v == null) return false;
                    return String(v).toLowerCase().includes(String(q).toLowerCase());
                })
            );
        }
        if (sort.field) {
            const sign = sort.dir === "desc" ? -1 : 1;
            r = [...r].sort((a, b) => {
                const av = resolve(a, sort.field);
                const bv = resolve(b, sort.field);
                if (av == null && bv == null) return 0;
                if (av == null) return 1;
                if (bv == null) return -1;
                if (isNumericString(av) && isNumericString(bv)) return (+av - +bv) * sign;
                const sa = String(av).toLowerCase();
                const sb = String(bv).toLowerCase();
                if (sa < sb) return -1 * sign;
                if (sa > sb) return 1 * sign;
                return 0;
            });
        }
        return r;
    }, [rows, filters, sort.field, sort.dir]);

    const hasAnyFilter = Object.values(filters).some((v) => v && String(v).trim() !== "");

    return {
        rows: view,
        sort, setSort,
        sortKey: sort.field, sortDir: sort.dir, toggleSort,
        filters, setFilter, clearFilters, hasAnyFilter,
    };
}

/* ---------------------------------------------------------- header element */

/**
 * <SortableTh field="x" ctrl={ctrl}>Header</SortableTh>
 *   OR legacy:
 * <SortableTh field="x" sort={sort} setSort={setSort}>Header</SortableTh>
 *
 * Props:
 *  - sortable (default true) — clicking label toggles sort
 *  - filterable (default true when ctrl is provided, false otherwise)
 *  - align: 'left' | 'right' | 'center'
 *  - className: extra classes for <th>
 *  - testIdPrefix (legacy alias used by older code)
 */
export function SortableTh({
    field,
    ctrl,
    sort: sortProp,
    setSort: setSortProp,
    children,
    className = "",
    testIdPrefix = "sort",
    align = "left",
    sortable = true,
    filterable,
}) {
    const sort = ctrl ? ctrl.sort : sortProp;
    const setSort = ctrl ? ctrl.setSort : setSortProp;
    const showFilter = filterable === undefined ? !!ctrl : !!filterable;

    const [open, setOpen] = useState(false);
    const popRef = useRef(null);
    useEffect(() => {
        if (!open) return;
        const onDown = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    const active = sort?.field === field;
    const dir = active ? sort.dir : null;
    const Icon = active ? (dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;

    const handleSortClick = () => {
        if (!sortable || !setSort) return;
        if (!active) { setSort({ field, dir: "asc" }); return; }
        if (dir === "asc") { setSort({ field, dir: "desc" }); return; }
        setSort({ field: null, dir: "asc" });
    };

    const filterVal = ctrl?.filters?.[field] || "";
    const hasFilter = String(filterVal).trim() !== "";

    const justify = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";

    return (
        <th className={`px-4 py-3 text-xs uppercase font-medium tracking-wider ${className}`} data-testid={`th-${field}`}>
            <div className={`flex items-center gap-1 ${justify}`}>
                {sortable ? (
                    <button
                        type="button"
                        onClick={handleSortClick}
                        data-testid={`${testIdPrefix}-${field}`}
                        className={`inline-flex items-center gap-1.5 hover:text-foreground transition-colors ${active ? "text-primary" : ""}`}
                        title="Click to sort"
                    >
                        <span>{children}</span>
                        <Icon className="w-3 h-3 opacity-70" strokeWidth={2} />
                    </button>
                ) : (
                    <span>{children}</span>
                )}
                {showFilter && ctrl && (
                    <div className="relative" ref={popRef}>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
                            data-testid={`filter-${field}`}
                            aria-label={`Filter ${field}`}
                            title="Filter column"
                            className={`p-0.5 transition-colors ${hasFilter ? "text-primary" : "opacity-40 hover:opacity-100"}`}
                        >
                            <Filter className="w-3 h-3" strokeWidth={2} />
                        </button>
                        {open && (
                            <div
                                className="absolute right-0 top-full mt-1 z-50 bg-popover text-popover-foreground border border-border shadow-lg p-2 w-52 normal-case"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="flex items-center gap-1">
                                    <input
                                        autoFocus
                                        type="text"
                                        value={filterVal}
                                        onChange={(e) => ctrl.setFilter(field, e.target.value)}
                                        placeholder="Filter…"
                                        className="flex-1 px-2 py-1 text-xs bg-background border border-border focus:outline-none focus:border-primary"
                                        data-testid={`filter-input-${field}`}
                                    />
                                    {hasFilter && (
                                        <button
                                            type="button"
                                            onClick={() => ctrl.setFilter(field, "")}
                                            className="p-1 text-muted-foreground hover:text-foreground"
                                            title="Clear"
                                            data-testid={`filter-clear-${field}`}
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </th>
    );
}

/* ------------------------------------------- alias re-export for new code */

export const SortFilterTh = SortableTh;

/**
 * <FilterChip ctrl={ctrl}/> — drop this anywhere above a table to surface a
 * one-glance summary of active filters with a single "Clear all" CTA. Renders
 * nothing when no filter is active, so it can be safely placed on every page.
 */
export function FilterChip({ ctrl, className = "" }) {
    if (!ctrl?.hasAnyFilter) return null;
    const active = Object.entries(ctrl.filters || {}).filter(([, v]) => v && String(v).trim() !== "");
    return (
        <div className={`inline-flex items-center gap-2 px-2.5 py-1 border border-primary/40 bg-primary/5 text-[10px] uppercase tracking-[0.16em] text-primary ${className}`}
             data-testid="filter-chip">
            <span>{active.length} filter{active.length === 1 ? "" : "s"} active</span>
            <span className="text-muted-foreground normal-case tracking-normal">
                {active.map(([k, v]) => `${k}: ${v}`).join(" · ")}
            </span>
            <button
                type="button"
                onClick={() => ctrl.clearFilters()}
                className="ml-1 text-muted-foreground hover:text-primary border-l border-primary/40 pl-2"
                data-testid="filter-chip-clear-all"
            >
                Clear all
            </button>
        </div>
    );
}
